<template>
  <div class="px-4 py-5">
    <!-- Phone required modal -->
    <div
      v-if="showPhoneModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    >
      <div class="card-panel w-full max-w-sm p-5 space-y-4">
        <div>
          <h2 class="text-base font-extrabold text-slate-800 text-left">Enter phone number</h2>
          <p class="text-xs text-slate-500 text-left mt-1">Add your phone number to continue posting a job.</p>
        </div>
        <div class="flex flex-col gap-1">
          <label class="field-label">Telegram Username</label>
          <input :value="phoneModalUsername" class="input-field bg-slate-50" readonly />
        </div>
        <div class="flex flex-col gap-1">
          <label class="field-label">Phone Number</label>
          <input
            v-model.trim="phoneInput"
            class="input-field"
            type="tel"
            inputmode="tel"
            placeholder="09xxxxxxxx"
          />
        </div>
        <p v-if="phoneError" class="text-rose-600 text-xs text-center">{{ phoneError }}</p>
        <button type="button" class="btn-primary" :disabled="phoneSaving" @click="savePhone">
          {{ phoneSaving ? 'Saving…' : 'Save' }}
        </button>
      </div>
    </div>

    <div class="card-panel p-5">
      <h1 class="text-lg font-extrabold text-slate-800 text-left mb-1">
        {{ step === 'preview' ? 'Preview Job' : 'Post a Job' }}
      </h1>
      <p class="text-xs text-slate-500 text-left mb-5">
        {{ step === 'preview'
          ? 'Review the details below, then submit.'
          : 'Fill in the details. Your Telegram username is filled automatically.' }}
      </p>

      <!-- Form step -->
      <form v-if="canShowForm && step === 'form'" class="space-y-4" @submit.prevent="goPreview">
        <div class="flex flex-col gap-1">
          <label class="field-label">Company Name</label>
          <input v-model.trim="form.company_name" class="input-field" placeholder="e.g. ABCD Bar And Restaurant..." required />
        </div>

        <div
          v-for="(entry, index) in jobEntries"
          :key="entry._key"
          class="space-y-4 rounded-xl border border-slate-100 bg-slate-50/60 p-3"
        >
          <div class="flex items-center justify-between gap-2">
            <p class="text-xs font-bold text-slate-600">Job {{ index + 1 }}</p>
            <button
              v-if="jobEntries.length > 1"
              type="button"
              class="text-[11px] font-bold text-rose-600"
              @click="removeJob(index)"
            >
              Remove
            </button>
          </div>
          <div class="flex flex-col gap-1">
            <label class="field-label">Job Title</label>
            <input v-model.trim="entry.title" class="input-field" placeholder="e.g. Cashier, Waiter..." required />
          </div>
          <div class="flex flex-col gap-1">
            <label class="field-label">Job Type</label>
            <select v-model="entry.job_type" class="input-field" required>
              <option disabled value="">Select job type</option>
              <option v-for="t in jobTypes" :key="t" :value="t">{{ t }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="field-label">Education Level</label>
            <select v-model="entry.education_level" class="input-field" required>
              <option disabled value="">Select education level</option>
              <option v-for="e in educationLevels" :key="e" :value="e">{{ e }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="field-label">Experience</label>
            <input v-model.trim="entry.experience" class="input-field" placeholder="e.g. 2+ years" required />
          </div>
          <div class="flex flex-col gap-1">
            <label class="field-label">Sex</label>
            <select v-model="entry.sex" class="input-field" required>
              <option disabled value="">Select</option>
              <option v-for="s in sexOptions" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="field-label">No Of Employees</label>
            <input v-model.trim="entry.no_of_employees" class="input-field" placeholder="e.g. 2" required />
          </div>
          <div class="flex flex-col gap-1" v-if="1==3">
            <label class="field-label">Sector</label>
            <select v-model="entry.sector" class="input-field">
              <option disabled value="">Select sector</option>
              <option v-for="s in sectors" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="field-label">Salary (Optional)</label>
            <input
              v-model.trim="entry.salary"
              class="input-field"
              placeholder="e.g. 40,000 ETB / Negotiable"
            />
          </div>
        </div>

        <button type="button" class="btn-secondary" @click="addJob">
          Add More Jobs +
        </button>

        <div class="flex flex-col gap-1">
          <label class="field-label">Job Deadline</label>
          <input v-model="form.job_deadline" class="input-field" type="date" required />
        </div>
        <div class="flex flex-col gap-1">
          <label class="field-label">Work Address</label>
          <select v-model="form.work_address" class="input-field" required>
            <option disabled value="">Select city</option>
            <option v-for="c in workAddresses" :key="c" :value="c">{{ c }}</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="field-label">Work Location</label>
          <input v-model.trim="form.work_location" class="input-field" placeholder="e.g. Bole, near Edna Mall" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="field-label">Phone Number (Optional)</label>
          <input v-model.trim="form.phone" class="input-field" placeholder="09xxxxxxxx" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="field-label">Telegram Username</label>
          <input v-model.trim="form.telegram_username" class="input-field bg-slate-50" readonly />
        </div>
        <div class="flex flex-col gap-1">
          <label class="field-label">Description</label>
          <textarea
            v-model.trim="form.description"
            class="input-field min-h-[120px]"
            placeholder="Describe the role, requirements, and location"
            required
          />
        </div>

        <p v-if="error" class="text-rose-600 text-xs text-center">{{ error }}</p>

        <button type="submit" class="btn-primary">Next</button>
      </form>

      <!-- Preview step -->
      <div v-else-if="canShowForm && step === 'preview'" class="space-y-4">
        <div class="rounded-xl border border-slate-100 bg-slate-50 p-4 text-left text-sm text-slate-700 space-y-3">
          <div v-for="(entry, index) in jobEntries" :key="'preview-' + entry._key" class="space-y-1">
            <p class="font-bold text-slate-800">{{ index + 1 }}. Job Title: {{ entry.title || '—' }}</p>
            <p><span class="font-bold text-slate-800">Job Type:</span> {{ entry.job_type || '—' }}</p>
            <p><span class="font-bold text-slate-800">Education Level:</span> {{ entry.education_level || '—' }}</p>
            <p><span class="font-bold text-slate-800">Experience:</span> {{ entry.experience || '—' }}</p>
            <p><span class="font-bold text-slate-800">Sex:</span> {{ entry.sex || '—' }}</p>
            <p><span class="font-bold text-slate-800">No Of Employees:</span> {{ entry.no_of_employees || '—' }}</p>
            <!-- <p><span class="font-bold text-slate-800">Sector:</span> {{ entry.sector || '—' }}</p> -->
            <p><span class="font-bold text-slate-800">Salary:</span> {{ entry.salary || '—' }}</p>
          </div>
          <div class="border-t border-slate-200 pt-3 space-y-1.5">
            <p v-for="row in commonPreviewRows" :key="row.label">
              <span class="font-bold text-slate-800">{{ row.label }}:</span>
              {{ row.value }}
            </p>
          </div>
        </div>

        <p v-if="error" class="text-rose-600 text-xs text-center">{{ error }}</p>
        <p v-if="success" class="text-emerald-600 text-xs text-center">{{ success }}</p>

        <div class="flex flex-col gap-2">
          <button type="button" class="btn-primary" :disabled="loading" @click="submit">
            {{ loading ? 'Submitting…' : 'Submit Job' }}
          </button>
          <button type="button" class="btn-secondary" :disabled="loading" @click="step = 'form'">
            Back to Edit
          </button>
        </div>
      </div>

      <p v-else-if="!checkingPhone" class="text-xs text-slate-500 text-center py-6">
        Enter your phone number to continue.
      </p>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { apiFetch } from '../utils/api'
import { joinJobValues } from '../utils/jobFields'
import { hapticLight } from '../utils/telegram'

const SECTOR_TEMPLATES = {
 Education: '',
Engineering: '',
Finance: '',
Healthcare: '',
Hospitality: '',
IT: '',
Logistics: '',
Marketing: '',
Sales: '',
  Other: '',}

const JOB_TYPES = ['Permanent', 'Part-time', 'Contract', 'Internship', 'Temporary', 'Freelance']
const EDUCATION_LEVELS = ['Any','Doctorate','PhD','Masters', 'Degree', 'Diploma', 'TVET','Certificate', 'High School', 'Under High School' ]
const SEX_OPTIONS = ['Male', 'Female', 'Both']
const WORK_ADDRESSES = [
  'Addis Ababa',
  'Adama',
  'Bahir Dar',
  'Hawassa',
  'Mekelle',
  'Dire Dawa',
  'Jimma',
  'Gondar',
  'Dessie',
  'Other',
]

let jobKeySeq = 1
function createJobEntry() {
  return reactive({
    _key: jobKeySeq++,
    title: '',
    job_type: '',
    education_level: '',
    experience: '',
    sex: '',
    no_of_employees: '',
    sector: '',
    salary: '',
  })
}

export default {
  name: 'PostJobView',
  setup() {
    const store = useStore()
    const router = useRouter()
    const sectors = ref([])
    const loading = ref(false)
    const error = ref('')
    const success = ref('')
    const step = ref('form')
    const checkingPhone = ref(true)
    const showPhoneModal = ref(false)
    const phoneInput = ref('')
    const phoneError = ref('')
    const phoneSaving = ref(false)
    const jobEntries = ref([createJobEntry()])

    const user = computed(() => store.getters.currentUser)
    const phoneModalUsername = computed(() => {
      const u = user.value?.username
      return u ? `@${String(u).replace(/^@/, '')}` : '—'
    })
    const canShowForm = computed(() => !!(user.value?.phone || '').trim())

    const form = reactive({
      company_name: '',
      job_deadline: '',
      work_address: '',
      work_location: '',
      phone: '',
      telegram_username: user.value?.username || '',
      description: '',
    })

    const commonPreviewRows = computed(() => [
      { label: 'Company Name', value: form.company_name || '—' },
      { label: 'Job Deadline', value: form.job_deadline || '—' },
      { label: 'Work Address', value: form.work_address || '—' },
      { label: 'Work Location', value: form.work_location || '—' },
      { label: 'Phone Number', value: form.phone || '—' },
      { label: 'Telegram Username', value: form.telegram_username ? `@${String(form.telegram_username).replace(/^@/, '')}` : '—' },
      { label: 'Description', value: form.description || '—' },
    ])

    function syncUserFields() {
      const u = user.value
      form.telegram_username = u?.username || ''
    }

    function addJob() {
      jobEntries.value.push(createJobEntry())
    }

    function removeJob(index) {
      if (jobEntries.value.length <= 1) return
      jobEntries.value.splice(index, 1)
    }

    async function loadProfile() {
      checkingPhone.value = true
      try {
        const res = await apiFetch('/auth/me')
        const data = await res.json().catch(() => ({}))
        if (res.ok && data.user) {
          store.commit('setUser', data.user)
        }
      } catch {
        // keep stored user
      } finally {
        syncUserFields()
        showPhoneModal.value = !canShowForm.value
        checkingPhone.value = false
      }
    }

    async function loadSectors() {
      try {
        const res = await apiFetch('/jobs/sectors')
        const data = await res.json()
        sectors.value = data.sectors || []
      } catch {
        sectors.value = ['IT', 'Finance', 'Sales', 'Marketing', 'Other']
      }
    }

    async function savePhone() {
      phoneError.value = ''
      const phone = phoneInput.value.trim()
      if (!phone) {
        phoneError.value = 'Phone number is required'
        return
      }
      if (phone.replace(/\D/g, '').length < 7) {
        phoneError.value = 'Enter a valid phone number'
        return
      }
      phoneSaving.value = true
      try {
        const res = await apiFetch('/auth/me/phone', {
          method: 'PATCH',
          body: JSON.stringify({ phone }),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Failed to save phone')
        store.commit('setUser', data.user)
        syncUserFields()
        showPhoneModal.value = false
        hapticLight()
      } catch (err) {
        phoneError.value = err.message || 'Failed to save phone'
      } finally {
        phoneSaving.value = false
      }
    }

    watch(
      () => jobEntries.value.map((e) => e.sector).join('|'),
      () => {
        const sector = jobEntries.value.find((e) => e.sector)?.sector
        if (!sector) return
        const template = SECTOR_TEMPLATES[sector]
        if (!template) return
        const current = (form.description || '').trim()
        const templates = Object.values(SECTOR_TEMPLATES)
        const canReplace = !current || templates.includes(current)
        if (canReplace) form.description = template
      },
    )

    function scrollPageToTop() {
      const main = document.querySelector('main.overflow-y-auto')
      if (main) main.scrollTop = 0
      window.scrollTo(0, 0)
    }

    function goPreview() {
      error.value = ''
      const incomplete = jobEntries.value.some(
        (e) =>
          !String(e.title || '').trim() ||
          !e.job_type ||
          !e.education_level ||
          !String(e.experience || '').trim() ||
          !e.sex ||
          !String(e.no_of_employees || '').trim(),
      )
      if (incomplete) {
        error.value = 'Please complete all required fields for each job.'
        return
      }
      step.value = 'preview'
      requestAnimationFrame(() => {
        scrollPageToTop()
      })
    }

    function buildPayload() {
      const entries = jobEntries.value
      return {
        company_name: form.company_name,
        title: joinJobValues(entries.map((e) => e.title)),
        job_type: joinJobValues(entries.map((e) => e.job_type)),
        education_level: joinJobValues(entries.map((e) => e.education_level)),
        experience: joinJobValues(entries.map((e) => e.experience)),
        sex: joinJobValues(entries.map((e) => e.sex)),
        no_of_employees: joinJobValues(entries.map((e) => e.no_of_employees)),
        sector: joinJobValues(entries.map((e) => e.sector)),
        salary: joinJobValues(entries.map((e) => e.salary)),
        job_deadline: form.job_deadline,
        work_address: form.work_address,
        work_location: form.work_location,
        phone: form.phone,
        telegram_username: form.telegram_username,
        description: form.description,
      }
    }

    async function submit() {
      error.value = ''
      success.value = ''
      loading.value = true
      try {
        const res = await apiFetch('/jobs', {
          method: 'POST',
          body: JSON.stringify(buildPayload()),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Failed to submit job')
        hapticLight()
        success.value = 'Job submitted successfully. Waiting for admin approval.'
        setTimeout(() => router.push({ name: 'my_jobs' }), 900)
      } catch (err) {
        error.value = err.message || 'Failed to submit'
      } finally {
        loading.value = false
      }
    }

    onMounted(async () => {
      await Promise.all([loadProfile(), loadSectors()])
    })

    return {
      form,
      jobEntries,
      sectors,
      jobTypes: JOB_TYPES,
      educationLevels: EDUCATION_LEVELS,
      sexOptions: SEX_OPTIONS,
      workAddresses: WORK_ADDRESSES,
      loading,
      error,
      success,
      step,
      checkingPhone,
      showPhoneModal,
      phoneInput,
      phoneError,
      phoneSaving,
      phoneModalUsername,
      canShowForm,
      commonPreviewRows,
      addJob,
      removeJob,
      savePhone,
      goPreview,
      submit,
    }
  },
}
</script>
